import { useEffect, useRef, useState } from "react";
import { api, apiError, downloadFile } from "../lib/api";
import { FileText, Upload, Trash2, Download } from "lucide-react";
import { toast } from "sonner";

export default function DocumentsSection({ studentId, canEdit, canDelete = canEdit }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => { api.get(`/students/${studentId}/documents`).then((r) => setDocs(r.data)).catch(() => {}); };
  useEffect(load, [studentId]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File exceeds 10MB limit"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/students/${studentId}/documents`, fd);
      toast.success(`${file.name} uploaded`);
      load();
    } catch (err) { toast.error(apiError(err)); }
    setUploading(false);
    e.target.value = "";
  };

  const del = async (d) => {
    try { await api.put(`/documents/${d.id}/delete`); toast.success("Document removed"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.ceil(b / 1024)} KB`);

  return (
    <div className="no-print bg-white border border-slate-200/60 rounded-xl p-5 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold text-slate-800">Support Documents</h3>
        {canEdit && (
          <>
            <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.csv,.txt,.xlsx" className="hidden" onChange={upload} data-testid="document-file-input" />
            <button data-testid="upload-document-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="flex items-center gap-1.5 text-xs font-semibold bg-emerald-700 text-white hover:bg-emerald-800 px-4 py-2.5 rounded-full transition-colors duration-150 disabled:opacity-60">
              <Upload size={14} /> {uploading ? "Uploading…" : "Upload"}
            </button>
          </>
        )}
      </div>
      {docs.length === 0 && <p className="text-sm text-slate-400">No documents attached. IEP-aligned support documents uploaded by TAG appear here for aides to reference.</p>}
      <ul className="divide-y divide-slate-100">
        {docs.map((d) => (
          <li key={d.id} data-testid={`document-row-${d.id}`} className="py-3 flex items-center gap-3">
            <FileText size={18} className="text-emerald-700 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800 truncate">{d.original_filename}</p>
              <p className="text-xs text-slate-400">{fmtSize(d.size)} · uploaded by {d.uploaded_by} · {d.created_at.slice(0, 10)}</p>
            </div>
            <button data-testid={`download-document-btn-${d.id}`} onClick={() => downloadFile(`/documents/${d.id}/download`, d.original_filename).catch(() => toast.error("Download failed"))}
              className="p-2 text-slate-400 hover:text-emerald-700 transition-colors duration-150"><Download size={16} /></button>
            {canDelete && (
              <button data-testid={`delete-document-btn-${d.id}`} onClick={() => del(d)}
                className="p-2 text-slate-300 hover:text-red-600 transition-colors duration-150"><Trash2 size={16} /></button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
